import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Spin, message, Button } from 'antd';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import {
  DollarOutlined,
  TransactionOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WalletOutlined,
  BankOutlined,
  HistoryOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import api from '../utils/api';
import axios from 'axios';

// Create axios instance for admin API
const adminApi = axios.create({
  baseURL: '/api',
  timeout: 10000,
});

// Request interceptor to add JWT token
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: on 401 clear session and redirect to login
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalTransactions: 0,
    totalAmount: 0,
    totalFee: 0,
    todayTransactions: 0,
    pendingTransactions: 0,
    cashInToday: 0,
    cashInMonth: 0,
    cashOutToday: 0,
    cashOutMonth: 0,
    revenueByMethod: [],
    dailyTransactions: [],
    recentTransactions: []
  });
  const [balanceStats, setBalanceStats] = useState({
    totalDepositBalance: 0,
    totalAvailableBalance: 0,
    totalPendingBalance: 0,
    totalFrozenBalance: 0
  });

  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    fetchBalanceStats();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([fetchDashboardData(), fetchBalanceStats()]);
      setLastUpdated(new Date());
      message.success('Data diperbarui');
    } finally {
      setRefreshing(false);
    }
  };

  const fetchBalanceStats = async () => {
    try {
      const response = await adminApi.get('/dashboard/balance-stats');
      setBalanceStats(response.data);
    } catch (error) {
      console.error('Balance stats fetch error:', error);
      if (error.response?.status === 401) message.error('Sesi berakhir, silakan login ulang');
    }
  };

  const fetchDashboardData = async () => {
    try {
      const response = await adminApi.get('/dashboard/stats');
      setStats(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      message.error(error.response?.status === 401 ? 'Sesi berakhir, silakan login ulang' : 'Gagal memuat data dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'green';
      case 'pending':
        return 'orange';
      case 'failed':
        return 'red';
      case 'cancelled':
        return 'default';
      case 'expired':
        return 'volcano';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'success':
        return 'Berhasil';
      case 'pending':
        return 'Menunggu';
      case 'failed':
        return 'Gagal';
      case 'cancelled':
        return 'Dibatalkan';
      case 'expired':
        return 'Kadaluarsa';
      default:
        return status;
    }
  };

  const columns = [
    {
      title: 'ID Transaksi',
      dataIndex: 'transaction_id',
      key: 'transaction_id',
    },
    {
      title: 'Pelanggan',
      dataIndex: 'customer',
      key: 'customer',
    },
    {
      title: 'Jumlah',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => formatCurrency(amount),
    },
    {
      title: 'Metode',
      dataIndex: 'payment_method',
      key: 'payment_method',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Tanggal',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleString('id-ID'),
    },
  ];

  const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="fade-in">
      <div style={{ marginBottom: '40px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--white)' }}>
            Dashboard Arendo
          </h1>
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '16px', margin: 0 }}>
            Ringkasan transaksi dan performa bisnis Anda secara real-time
          </p>
          {lastUpdated && (
            <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px', marginTop: 6, marginBottom: 0 }}>
              Terakhir diperbarui: {lastUpdated.toLocaleTimeString('id-ID')}
            </p>
          )}
        </div>
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
          loading={refreshing}
        >
          Reload
        </Button>
      </div>

      {/* Stats Cards */}
      <Row gutter={[24, 24]} style={{ marginBottom: '40px' }}>
        <Col xs={24} sm={12} md={8}>
          <div className="stat-card fade-in">
            <div className="modern-statistic">
              <Statistic
                title="Transaksi Sukses"
                value={stats.totalTransactions}
                prefix={<TransactionOutlined style={{ color: 'var(--primary)' }} />}
                valueStyle={{ color: 'var(--primary)', fontWeight: 700 }}
              />
              <div style={{ marginTop: 4, fontSize: 12, color: '#94a3b8' }}>Bulan ini</div>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div className="stat-card info fade-in">
            <div className="modern-statistic modern-statistic--nominal">
              <Statistic
                title="Total Nilai Transaksi"
                value={stats.totalAmount}
                prefix={<DollarOutlined style={{ color: 'var(--info)' }} />}
                formatter={(value) => formatCurrency(value)}
                valueStyle={{ color: 'var(--info)', fontWeight: 700 }}
              />
              <div style={{ marginTop: 4, fontSize: 12, color: '#94a3b8' }}>Bulan ini</div>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div className="stat-card success fade-in">
            <div className="modern-statistic modern-statistic--nominal">
              <Statistic
                title="Total Pendapatan (Fee)"
                value={stats.totalFee}
                prefix={<DollarOutlined style={{ color: 'var(--success)' }} />}
                formatter={(value) => formatCurrency(value)}
                valueStyle={{ color: 'var(--success)', fontWeight: 700 }}
              />
              <div style={{ marginTop: 4, fontSize: 12, color: '#94a3b8' }}>Bulan ini</div>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={12}>
          <div className="stat-card fade-in">
            <div className="modern-statistic">
              <Statistic
                title="Transaksi Berhasil"
                value={stats.todayTransactions}
                prefix={<CheckCircleOutlined style={{ color: 'var(--primary)' }} />}
                valueStyle={{ color: 'var(--primary)', fontWeight: 700 }}
              />
              <div style={{ marginTop: 4, fontSize: 12, color: '#94a3b8' }}>Hari ini</div>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={12}>
          <div className="stat-card warning fade-in">
            <div className="modern-statistic">
              <Statistic
                title="Transaksi Menunggu (Processing)"
                value={stats.pendingTransactions}
                prefix={<ClockCircleOutlined style={{ color: 'var(--warning)' }} />}
                valueStyle={{ color: 'var(--warning)', fontWeight: 700 }}
              />
              <div style={{ marginTop: 4, fontSize: 12, color: '#94a3b8' }}>Hari ini</div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Nominal Cash In / Cash Out */}
      <Row gutter={[24, 24]} style={{ marginBottom: '40px' }}>
        <Col xs={24} sm={12} md={6}>
          <div className="stat-card info fade-in">
            <div className="modern-statistic modern-statistic--nominal">
              <Statistic
                title="Cash In Sukses"
                value={stats.cashInToday}
                prefix={<ArrowDownOutlined style={{ color: 'var(--info)' }} />}
                formatter={(value) => formatCurrency(value)}
                valueStyle={{ color: 'var(--info)', fontWeight: 700 }}
              />
              <div style={{ marginTop: 4, fontSize: 12, color: '#94a3b8' }}>Hari ini (nominal)</div>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div className="stat-card info fade-in">
            <div className="modern-statistic modern-statistic--nominal">
              <Statistic
                title="Cash In Sukses"
                value={stats.cashInMonth}
                prefix={<ArrowDownOutlined style={{ color: 'var(--info)' }} />}
                formatter={(value) => formatCurrency(value)}
                valueStyle={{ color: 'var(--info)', fontWeight: 700 }}
              />
              <div style={{ marginTop: 4, fontSize: 12, color: '#94a3b8' }}>Bulan ini (nominal)</div>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div className="stat-card warning fade-in">
            <div className="modern-statistic modern-statistic--nominal">
              <Statistic
                title="Cash Out Sukses"
                value={stats.cashOutToday}
                prefix={<ArrowUpOutlined style={{ color: 'var(--warning)' }} />}
                formatter={(value) => formatCurrency(value)}
                valueStyle={{ color: 'var(--warning)', fontWeight: 700 }}
              />
              <div style={{ marginTop: 4, fontSize: 12, color: '#94a3b8' }}>Hari ini (nominal)</div>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div className="stat-card warning fade-in">
            <div className="modern-statistic modern-statistic--nominal">
              <Statistic
                title="Cash Out Sukses"
                value={stats.cashOutMonth}
                prefix={<ArrowUpOutlined style={{ color: 'var(--warning)' }} />}
                formatter={(value) => formatCurrency(value)}
                valueStyle={{ color: 'var(--warning)', fontWeight: 700 }}
              />
              <div style={{ marginTop: 4, fontSize: 12, color: '#94a3b8' }}>Bulan ini (nominal)</div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Charts */}
      {/* Balance Overview Section */}
      <Row gutter={[16, 16]} style={{ marginBottom: '32px' }}>
        <Col xs={24}>
          <div className="stat-card fade-in" style={{
            padding: '24px'
          }}>
            <h3 style={{
              marginBottom: '20px',
              color: 'var(--dark)',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <WalletOutlined style={{ color: 'var(--primary)' }} />
              Ringkasan Saldo Merchant
            </h3>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <WalletOutlined
                    style={{
                      fontSize: '32px',
                      color: '#52c41a',
                      marginBottom: '8px',
                      display: 'block'
                    }}
                  />
                  <Statistic
                    title="Total Deposit Balance"
                    value={balanceStats.totalDepositBalance}
                    formatter={(value) => formatCurrency(value)}
                    valueStyle={{
                      color: '#52c41a',
                      fontWeight: 700,
                      fontSize: '18px'
                    }}
                  />
                  <div style={{
                    fontSize: '12px',
                    color: '#666',
                    marginTop: '4px',
                    fontStyle: 'italic'
                  }}>
                    Saldo untuk cash out langsung
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <BankOutlined
                    style={{
                      fontSize: '32px',
                      color: '#1890ff',
                      marginBottom: '8px',
                      display: 'block'
                    }}
                  />
                  <Statistic
                    title="Total Available Balance"
                    value={balanceStats.totalAvailableBalance}
                    formatter={(value) => formatCurrency(value)}
                    valueStyle={{
                      color: '#1890ff',
                      fontWeight: 700,
                      fontSize: '18px'
                    }}
                  />
                  <div style={{
                    fontSize: '12px',
                    color: '#666',
                    marginTop: '4px',
                    fontStyle: 'italic'
                  }}>
                    Saldo dari transaksi settle
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <HistoryOutlined
                    style={{
                      fontSize: '32px',
                      color: '#faad14',
                      marginBottom: '8px',
                      display: 'block'
                    }}
                  />
                  <Statistic
                    title="Total Pending Balance"
                    value={balanceStats.totalPendingBalance}
                    formatter={(value) => formatCurrency(value)}
                    valueStyle={{
                      color: '#faad14',
                      fontWeight: 700,
                      fontSize: '18px'
                    }}
                  />
                  <div style={{
                    fontSize: '12px',
                    color: '#666',
                    marginTop: '4px',
                    fontStyle: 'italic'
                  }}>
                    Saldo menunggu settlement
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <DollarOutlined
                    style={{
                      fontSize: '32px',
                      color: '#722ed1',
                      marginBottom: '8px',
                      display: 'block'
                    }}
                  />
                  <Statistic
                    title="Total All Balance"
                    value={balanceStats.totalDepositBalance + balanceStats.totalAvailableBalance + balanceStats.totalPendingBalance}
                    formatter={(value) => formatCurrency(value)}
                    valueStyle={{
                      color: '#722ed1',
                      fontWeight: 700,
                      fontSize: '18px'
                    }}
                  />
                  <div style={{
                    fontSize: '12px',
                    color: '#666',
                    marginTop: '4px',
                    fontStyle: 'italic'
                  }}>
                    Total keseluruhan saldo
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={16}>
          <div className="chart-container">
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--dark)' }}>
              Tren Transaksi 7 Hari Terakhir
            </h3>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={stats.dailyTransactions}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="date"
                  stroke="#64748b"
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  style={{ fontSize: '12px' }}
                />
                <YAxis
                  stroke="#64748b"
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  style={{ fontSize: '12px' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                  formatter={(value, name) => [
                    name === 'transactions' ? value : formatCurrency(value),
                    name === 'transactions' ? 'Transaksi' : 'Pendapatan'
                  ]}
                />
                <Line
                  type="monotone"
                  dataKey="transactions"
                  stroke="#6366f1"
                  strokeWidth={3}
                  dot={{ fill: '#6366f1', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, fill: '#4f46e5' }}
                />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#10b981"
                  strokeWidth={3}
                  dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, fill: '#059669' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Col>
        <Col xs={24} lg={8}>
          <div className="chart-container">
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--dark)' }}>
              Pendapatan per Metode
            </h3>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={stats.revenueByMethod.map(item => ({
                    ...item,
                    revenue: parseFloat(item.revenue) || 0,
                    shortName: item.name.length > 15 ? item.name.substring(0, 12) + '...' : item.name
                  }))}
                  cx="50%"
                  cy="45%"
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="revenue"
                  label={({ shortName, percent }) => `${shortName} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                  style={{
                    fontSize: '11px',
                    fontWeight: '600'
                  }}
                >
                  {stats.revenueByMethod.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                      stroke="#ffffff"
                      strokeWidth={3}
                      className="recharts-pie-sector"
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }}
                  formatter={(value, name) => [
                    <strong>{formatCurrency(value)}</strong>,
                    <div style={{ color: '#64748b', fontSize: '12px' }}>{name}</div>
                  ]}
                />
                <Legend
                  verticalAlign="bottom"
                  height={80}
                  layout="horizontal"
                  wrapperStyle={{
                    paddingTop: '20px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}
                  formatter={(value, entry) => {
                    return <span style={{ fontSize: '12px' }}>{value}</span>;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Col>
      </Row>

      {/* Recent Transactions */}
      <div className="chart-container">
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--dark)' }}>
          Transaksi Terbaru
        </h3>
        <Table
          columns={columns}
          dataSource={stats.recentTransactions}
          rowKey="transaction_id"
          pagination={false}
          scroll={{ x: true }}
          className="modern-table"
          style={{
            background: 'var(--white)',
            borderRadius: '12px',
            overflow: 'hidden'
          }}
          rowClassName="hover:bg-gray-50 transition-colors duration-200"
        />
      </div>
    </div>
  );
};

export default Dashboard;